import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowClockwise, ArrowLeft, DownloadSimple, Warning } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import StandardTable from '../components/ui/StandardTable';
import { Mono } from '../components/ui/Mono';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { FormatInt } from '../utils/format';
import {
    FormatarDiaCurto,
    SITUACAO_ORDEM,
    SITUACAO_ROTULO,
    SITUACAO_VARIANT,
    SUBSTATUS_ROTULO,
} from '../constants/coletas';

// ─────────────────────────────────────────────────────────────────────────────
// LISTAGEM DE ENVIOS DE UM DIA — /coletas/envios/:dia
//
// A tela da pergunta do motorista: "levou tudo?". Todos os shipments com SLA
// no dia, em qualquer estado, com a trilha de cada um: impresso (quando e por
// onde — lote, OS/packing, ZIP), NF, o que o ML acha, e o momento em que saiu.
// Se faltou um, dá para baixar o ZPL na hora (a CÓPIA que o zpl-checkout já
// baixou — o melidash nunca baixa do ML) e ver o produto na composição.
// ─────────────────────────────────────────────────────────────────────────────

const ColetasEnvioDiaPage = () => {
    const { dia } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { checkPermission } = useAuth();
    const podeOperar = checkPermission('wms:operate');

    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [situacaoFiltro, setSituacaoFiltro] = useState(null);
    const [baixandoZpl, setBaixandoZpl] = useState(null);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            setDados(await api.getColetasEnviosDoDia(dia));
        } catch (error) {
            toast.error(error?.message || 'Não foi possível carregar os envios do dia.');
        } finally {
            setCarregando(false);
        }
    }, [dia, toast]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const baixarZpl = useCallback(
        async (shipmentId) => {
            setBaixandoZpl(shipmentId);
            try {
                const zpl = await api.getColetasZpl(shipmentId);
                const blob = new Blob([zpl], { type: 'text/plain;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `etiqueta-${shipmentId}.zpl`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);
            } catch (error) {
                toast.error(error?.message || 'Etiqueta não disponível.');
            } finally {
                setBaixandoZpl(null);
            }
        },
        [toast],
    );

    const linhas = useMemo(() => {
        const todas = dados?.results ?? [];
        return situacaoFiltro ? todas.filter((linha) => linha.situacao === situacaoFiltro) : todas;
    }, [dados, situacaoFiltro]);

    const colunas = useMemo(
        () => [
            {
                key: 'shipment_id',
                label: 'Shipment',
                render: (row) => <Mono className="text-xs">{row.shipment_id}</Mono>,
            },
            {
                key: 'composicao',
                label: 'Composição',
                render: (row) => (
                    <span
                        className="block max-w-[26rem] truncate font-mono text-xs text-mono-600 dark:text-mono-400"
                        title={row.composicao}
                    >
                        {row.composicao}
                    </span>
                ),
            },
            { key: 'unidades', label: 'Un.', align: 'right', getValue: (row) => FormatInt(row.unidades) },
            {
                key: 'situacao',
                label: 'Situação',
                getValue: (row) => SITUACAO_ROTULO[row.situacao] || row.situacao,
                render: (row) => (
                    <Badge variant={SITUACAO_VARIANT[row.situacao] || 'default'}>
                        {SITUACAO_ROTULO[row.situacao] || row.situacao}
                    </Badge>
                ),
            },
            { key: 'via', label: 'Via', getValue: (row) => row.via || '—' },
            { key: 'nf', label: 'NF', getValue: (row) => row.nf || '—' },
            { key: 'impresso_em', label: 'Impresso em', getValue: (row) => row.impresso_em || '—' },
            {
                key: 'enviado_em',
                label: 'Enviado',
                getValue: (row) => row.enviado_em || (row.enviado ? 'sim' : '—'),
                render: (row) =>
                    row.enviado ? (
                        <span className="text-sm text-success-text">{row.enviado_em || 'sim'}</span>
                    ) : (
                        <span className="text-sm text-mono-400">—</span>
                    ),
            },
            {
                key: 'shipment_substatus',
                label: 'ML diz',
                getValue: (row) =>
                    SUBSTATUS_ROTULO[row.shipment_substatus] || row.shipment_substatus || row.shipment_status || '—',
            },
            { key: 'dest_nome', label: 'Destinatário', getValue: (row) => row.dest_nome || '—' },
            {
                key: 'acoes',
                label: '',
                sortable: false,
                searchable: false,
                exportable: false,
                render: (row) => (
                    <div className="flex justify-end gap-1.5">
                        {podeOperar && row.zpl_baixado ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                disabled={baixandoZpl === row.shipment_id}
                                onClick={() => baixarZpl(row.shipment_id)}
                                title="Baixar o ZPL que o zpl-checkout já tem (reimpressão)"
                            >
                                <DownloadSimple size={14} className="mr-1" />
                                ZPL
                            </Button>
                        ) : null}
                    </div>
                ),
            },
        ],
        [podeOperar, baixandoZpl, baixarZpl],
    );

    const resumo = dados?.resumo ?? {};

    return (
        <div className="space-y-6">
            <PageHeader
                title={`Envios de ${FormatarDiaCurto(dia)}`}
                subtitle={`Todos os shipments com prazo de envio (SLA) em ${dia}, em qualquer estado — para conferir se saiu tudo e agir no que faltou.`}
                actions={
                    <div className="flex flex-wrap gap-2">
                        <Button variant="secondary" onClick={() => navigate('/coletas')}>
                            <ArrowLeft size={16} className="mr-1.5" />
                            Coletas
                        </Button>
                        <Button variant="secondary" onClick={carregar}>
                            <ArrowClockwise size={16} className="mr-1.5" />
                            Atualizar
                        </Button>
                    </div>
                }
            />

            {carregando ? (
                <Skeleton className="h-96 w-full" />
            ) : dados?.habilitado === false ? (
                <div className="rounded-xl border border-warning-200 bg-warning-soft p-4 text-sm text-warning-text dark:border-warning-900 dark:bg-warning-900/20">
                    <Warning size={16} className="mr-1.5 inline" />
                    {dados.motivo}
                </div>
            ) : (
                <>
                    {/* Badges-resumo clicáveis: clicar filtra a tabela pela situação. */}
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setSituacaoFiltro(null)}
                            className={situacaoFiltro === null ? '' : 'opacity-60'}
                        >
                            <Badge variant="default" className="text-sm">
                                Todos: {FormatInt(dados?.total ?? 0)}
                            </Badge>
                        </button>
                        {SITUACAO_ORDEM.filter((chave) => resumo[chave]).map((chave) => (
                            <button
                                key={chave}
                                type="button"
                                onClick={() => setSituacaoFiltro(situacaoFiltro === chave ? null : chave)}
                                className={situacaoFiltro === null || situacaoFiltro === chave ? '' : 'opacity-50'}
                                title="Clique para filtrar"
                            >
                                <Badge variant={SITUACAO_VARIANT[chave] || 'default'} className="text-sm">
                                    {SITUACAO_ROTULO[chave] || chave}: {FormatInt(resumo[chave])}
                                </Badge>
                            </button>
                        ))}
                    </div>

                    <StandardTable
                        columns={colunas}
                        rows={linhas}
                        rowKey={(row, indice) => row.shipment_id || indice}
                        countLabel="envios"
                        exportFileName={`coletas-envios-${dia}`}
                        searchPlaceholder="Buscar por shipment, SKU, NF, destinatário..."
                        emptyMessage="Nenhum envio com SLA neste dia."
                    />
                </>
            )}
        </div>
    );
};

export default ColetasEnvioDiaPage;
